<p-dialog
    [modal]="true"
    [closable]="false"
    styleClass="w-[90vw] max-w-[600px]"
    [(visible)]="isModalVisible"
    appendTo="body"
>
    <ng-template pTemplate="header">
        <div class="flex justify-between items-center w-full">
            <span class="font-bold">{{
                (modalProps.anchor ? 'Edit' : 'Create') + ' ' + modalProps.title
            }}</span>
            <p-button
                icon="pi pi-times"
                [text]="true"
                [rounded]="true"
                severity="secondary"
                (click)="onClose()"
            ></p-button>
        </div>
    </ng-template>

    @if (form && modalProps && form.controls) {
        <form [formGroup]="form" class="my-6 flex flex-col gap-6">
            @for (field of getAllFields(); track field) {
                @if (form.get(field.field) && !field.hidden) {
                    <!-- Lazy Dropdown -->
                    @if (field.type === 'lazy-dropdown') {
                        <div class="flex flex-col gap-1">
                            <label [for]="field.field">
                                {{ field.label }}
                                @if (field.required) {
                                    <span>*</span>
                                }
                            </label>
                            <app-dropdown-lazy
                                ngDefaultControl
                                [optionLabel]="getDropdownConfig(field.field).optionLabel"
                                optionValue="key"
                                [formControl]="$any(form.get(field.field))"
                                [isLoading]="getDropdownConfig(field.field).isLoading"
                                [options]="getDropdownConfig(field.field).options"
                                [hasMore]="getDropdownConfig(field.field).hasMore"
                                (hideEvent)="onFieldBlur(field.field)"
                                (loadMoreEvent)="onDropdownLoadMore(field.field, $event)"
                                (searchEvent)="onDropdownSearch(field.field, $event)"
                            ></app-dropdown-lazy>
                            @if (
                                form.get(field.field)?.hasError('required') &&
                                form.get(field.field)?.touched
                            ) {
                                <small class="text-red-500 text-xs"
                                    >{{ field.label }} is required</small
                                >
                            }
                        </div>
                    }
                    <!-- Regular Dropdown -->
                    @if (field.type === 'dropdown') {
                        <div class="flex flex-col gap-1">
                            <label [for]="field.field">
                                {{ field.label }}
                                @if (field.required) {
                                    <span>*</span>
                                }
                            </label>
                            <p-select
                                [formControlName]="field.field"
                                [options]="field.options"
                                [optionLabel]="field.optionLabel || 'label'"
                                [optionValue]="field.optionValue || 'value'"
                                [placeholder]="field.placeholder || ''"
                                styleClass="w-full"
                                (onBlur)="onFieldBlur(field.field)"
                            ></p-select>
                            @if (
                                form.get(field.field)?.hasError('required') &&
                                form.get(field.field)?.touched
                            ) {
                                <small class="text-red-500 text-xs"
                                    >{{ field.label }} is required</small
                                >
                            }
                        </div>
                    }
                    <!-- Text / Number Input -->
                    @if (field.type === 'text' || field.type === 'number') {
                        <div class="flex flex-col gap-1">
                            <div class="flex items-center gap-2">
                                <label [for]="field.field">
                                    {{ field.label }}
                                    @if (field.required) {
                                        <span>*</span>
                                    }
                                </label>
                                @if (field.tooltip) {
                                    <button
                                        type="button"
                                        class="inline-flex cursor-help items-center text-muted-color"
                                        [attr.aria-label]="field.label + ' information'"
                                        [pTooltip]="field.tooltip"
                                        tooltipPosition="right"
                                        tooltipEvent="both"
                                        appendTo="body"
                                    >
                                        <i class="pi pi-question-circle" aria-hidden="true"></i>
                                    </button>
                                }
                            </div>
                            <input
                                pInputText
                                class="w-full"
                                [id]="field.field"
                                [formControlName]="field.field"
                                [name]="field.field"
                                [type]="field.type"
                                [invalid]="
                                    form.get(field.field)?.invalid && form.get(field.field)?.touched
                                "
                                [attr.aria-invalid]="
                                    form.get(field.field)?.invalid && form.get(field.field)?.touched
                                "
                                [placeholder]="field.placeholder || ''"
                                [min]="field.type === 'number' ? 0 : null"
                                [step]="field.type === 'number' ? 1 : null"
                                (keydown)="preventNegativeInput($event, field)"
                                (blur)="onFieldBlur(field.field)"
                            />
                            @if (
                                form.get(field.field)?.hasError('required') &&
                                form.get(field.field)?.touched
                            ) {
                                <small class="text-red-500 text-xs"
                                    >{{ field.label }} is required</small
                                >
                            }
                            @if (form.get(field.field)?.hasError('conflict')) {
                                <small class="text-red-500 text-xs">
                                    {{ form.get(field.field)?.getError('conflict') }}
                                </small>
                            }
                            @if (
                                form.get(field.field)?.hasError('integer') &&
                                form.get(field.field)?.touched
                            ) {
                                <small class="text-red-500 text-xs">
                                    {{ field.label }} must be an integer
                                </small>
                            }
                            @if (
                                form.get(field.field)?.hasError('int64Range') &&
                                form.get(field.field)?.touched
                            ) {
                                <small class="text-red-500 text-xs">
                                    {{ int64RangeMessage }}
                                </small>
                            }
                            @if (
                                form.get(field.field)?.hasError('nameFormat') &&
                                form.get(field.field)?.dirty
                            ) {
                                <small class="text-red-500 text-xs">
                                    {{ field.label }} must start with a letter or underscore and
                                    contain only letters, numbers, and underscores
                                </small>
                            }
                        </div>
                    }
                    <!-- Textarea -->
                    @if (field.type === 'textarea') {
                        <div class="flex flex-col gap-1">
                            <label [for]="field.field">
                                {{ field.label }}
                                @if (field.required) {
                                    <span>*</span>
                                }
                            </label>
                            <textarea
                                pInputTextarea
                                class="w-full min-h-[80px]"
                                [id]="field.field"
                                [formControlName]="field.field"
                                [name]="field.field"
                                [rows]="field.rows || 5"
                                [placeholder]="field.placeholder || ''"
                                [autoResize]="true"
                                (blur)="onFieldBlur(field.field)"
                            ></textarea>
                            @if (
                                form.get(field.field)?.hasError('required') &&
                                form.get(field.field)?.touched
                            ) {
                                <small class="text-red-500 text-xs"
                                    >{{ field.label }} is required</small
                                >
                            }
                        </div>
                    }
                    <!-- Switch -->
                    @if (field.type === 'switch') {
                        <div class="flex flex-col gap-2">
                            <label [for]="field.field">{{ field.label }}</label>
                            <p-toggleSwitch [formControlName]="field.field"></p-toggleSwitch>
                        </div>
                    }
                    <!-- Input Mask -->
                    @if (field.type === 'mask') {
                        <div class="flex flex-col gap-1">
                            <label [for]="field.field">
                                {{ field.label }}
                                @if (field.required) {
                                    <span>*</span>
                                }
                            </label>
                            <p-inputMask
                                [id]="field.field"
                                [formControlName]="field.field"
                                [mask]="field.mask"
                                [name]="field.field"
                                [placeholder]="field.placeholder || ''"
                                styleClass="w-full"
                                slotChar="_"
                                (onBlur)="onFieldBlur(field.field)"
                            ></p-inputMask>
                            @if (
                                form.get(field.field)?.hasError('required') &&
                                form.get(field.field)?.touched
                            ) {
                                <small class="text-red-500 text-xs"
                                    >{{ field.label }} is required</small
                                >
                            }
                        </div>
                    }
                    @if (field.field === modalProps.insertContentAfter) {
                        <ng-content></ng-content>
                    }
                }
            }
        </form>
    }

    <ng-template pTemplate="footer">
        <div class="flex w-full flex-col gap-3">
            <ng-content select="[modal-validation]"></ng-content>
            <div class="flex justify-end gap-2">
                <p-button label="Cancel" severity="secondary" (click)="onClose()"></p-button>
                <p-button
                    label="Save"
                    [disabled]="isSaveDisabled()"
                    [loading]="isCreating"
                    (click)="saveAndClose()"
                ></p-button>
            </div>
        </div>
    </ng-template>
</p-dialog>
